import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PostCard } from "@/components/posts/PostCard";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export default function MyPostsPage() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("__all__");

    useEffect(() => {
        (async () => {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter !== "__all__") params.set("status", statusFilter);
            try {
                const res = await api.get(`/me/posts?${params.toString()}`);
                setPosts(res.data);
            } finally {
                setLoading(false);
            }
        })();
    }, [statusFilter]);

    return (
        <div className="space-y-6" data-testid="me-posts-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">Your posts</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                        {user?.role === "SHOP_OWNER" ? "My offers" : "My posts"}
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-40" data-testid="me-status-filter">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All statuses</SelectItem>
                            <SelectItem value="DRAFT">Drafts</SelectItem>
                            <SelectItem value="PUBLISHED">Live</SelectItem>
                            <SelectItem value="EXPIRED">Expired</SelectItem>
                            <SelectItem value="ARCHIVED">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                    <Link to="/posts/new">
                        <Button className="nest-gradient text-white" data-testid="me-create-btn">
                            <Plus size={16} className="mr-2" /> New
                        </Button>
                    </Link>
                </div>
            </header>

            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-72 rounded-2xl" />
                    ))}
                </div>
            ) : posts.length ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {posts.map((p) => (
                        <PostCard key={p.id} post={p} showStatus />
                    ))}
                </div>
            ) : (
                <EmptyState
                    title="You haven't posted anything yet"
                    hint="Create your first offer or event."
                    action={
                        <Link to="/posts/new">
                            <Button className="nest-gradient text-white">
                                <Plus size={16} className="mr-2" /> Create post
                            </Button>
                        </Link>
                    }
                />
            )}
        </div>
    );
}
